import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { UserPlus, Edit, Trash2, MapPin, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import AddUserModal from './AddUserModal';
import EditUserModal from './EditUserModal';
import InviteUserModal from './InviteUserModal';
import { format } from 'date-fns';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useUsers } from '../hooks/useUsers';
import { useUserManagement } from '../hooks/useUserManagement';
import type { User } from '../types';
import { useOrgInvites } from '../hooks/useOrgInvites';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';
import { roleBadgeVariant, roleDisplayLabel } from '../utils/role-style';

interface UserManagementProps {
  /**
   * Forces client mode when true; omitted follows the server's pure-client verdict.
   * The onboarding invite step passes true for a client org.
   */
  clientMode?: boolean;
}

export default function UserManagement({ clientMode }: UserManagementProps = {}) {
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { session: _session } = useAuth();
  const { t } = useTranslation('settings');

  // Use hooks for data and operations
  const { users, isLoading, error: fetchError } = useUsers();
  const { deleteUser, toggleUserStatus } = useUserManagement();
  const { invitesQuery, resend, cancel } = useOrgInvites();

  // A pure-client 3PL org has one login type. See utils/role-style.ts — the
  // membership is still `Admin`; only the label and the badge colour change.
  // `clientMode` (onboarding invite step) forces it for a new client org whose
  // client junction does not exist yet.
  const { isPureClient } = useOrgRoleSurfaces();
  const isClientMode = clientMode ?? isPureClient;

  const handleEdit = (user: typeof users extends (infer U)[] | undefined ? U : never) => {
    setSelectedUser(user as User);
    setEditModalOpen(true);
  };

  const handleDelete = (user: typeof users extends (infer U)[] | undefined ? U : never) => {
    setSelectedUser(user as User);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedUser) {
      deleteUser.mutate(
        { userId: selectedUser.id },
        {
          onSuccess: () => {
            setDeleteDialogOpen(false);
            setSelectedUser(null);
          },
        }
      );
    }
  };

  const getRoleBadgeColor = (role: string) => roleBadgeVariant(role, isClientMode);
  const getRoleLabel = (role: string) => roleDisplayLabel(role, isClientMode);

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>User Management</CardTitle>
            <CardDescription>
              {isClientMode
                ? 'Everyone you invite has the same access to this organization.'
                : 'Manage users and their access to locations'}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setInviteModalOpen(true)}>
              <Mail className="mr-2 h-4 w-4" />
              Invite User
            </Button>
            <Button onClick={() => setAddModalOpen(true)}>
              <UserPlus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="relative overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Locations</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
                        Loading users...
                      </div>
                    </TableCell>
                  </TableRow>
                ) : fetchError ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-destructive">
                      Error loading users: {fetchError.message}
                    </TableCell>
                  </TableRow>
                ) : users?.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  users?.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell className="font-medium">
                        {user.first_name} {user.last_name}
                      </TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>
                        <Badge variant={getRoleBadgeColor(user.role ?? 'User')}>
                          {getRoleLabel(user.role ?? 'User')}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {!user.profile_locations || user.profile_locations.length === 0 ? (
                            <span className="text-muted-foreground text-sm">No locations</span>
                          ) : (
                            user.profile_locations.map((pl) => {
                              if (!pl.location) return null;
                              return (
                                <Badge key={pl.location.id} variant="outline" className="text-xs">
                                  <MapPin className="mr-1 h-3 w-3" />
                                  {pl.location.name}
                                </Badge>
                              );
                            })
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={user.is_active ? 'success' : 'secondary'}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {user.created_at ? format(new Date(user.created_at), 'MMM d, yyyy') : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        {user.is_platform_admin ? (
                          // A platform admin: managed by PrintRun, never from a
                          // client org (the backend refuses every action anyway).
                          <Badge variant="outline" className="text-xs">
                            {t('users.platformSupport')}
                          </Badge>
                        ) : (
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(user)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant={user.is_active ? 'destructive' : 'secondary'}
                              size="sm"
                              onClick={() => toggleUserStatus.mutate({ userId: user.id, isActive: !user.is_active })}
                              className={user.is_active ? '' : ''}
                            >
                              {user.is_active ? 'Deactivate' : 'Activate'}
                            </Button>
                            <RemoveFromOrgButton userId={user.id} />
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(user)}
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Pending Invites */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>Pending Invites</CardTitle>
          <CardDescription>Invitations awaiting acceptance</CardDescription>
        </CardHeader>
        <CardContent>
          {invitesQuery.isLoading ? (
            <div className="text-sm text-muted-foreground">Loading invites…</div>
          ) : (invitesQuery.data?.length ?? 0) === 0 ? (
            <div className="text-sm text-muted-foreground">No pending invites</div>
          ) : (
            <div className="relative overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Expires</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invitesQuery.data?.map((inv: { id: string; email: string; role: string; expires_at: string | null }) => (
                    <TableRow key={inv.id}>
                      <TableCell>{inv.email}</TableCell>
                      <TableCell>
                        <Badge variant={getRoleBadgeColor(inv.role)}>{getRoleLabel(inv.role)}</Badge>
                      </TableCell>
                      <TableCell>{inv.expires_at ? format(new Date(inv.expires_at), 'MMM d, yyyy') : '-'}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => resend.mutate({ inviteId: inv.id })}>
                            Resend
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => cancel.mutate({ inviteId: inv.id })}>
                            Cancel
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <AddUserModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        clientMode={clientMode}
      />

      <InviteUserModal
        open={inviteModalOpen}
        onOpenChange={setInviteModalOpen}
        clientMode={clientMode}
      />


      {selectedUser && (
        <EditUserModal
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          user={selectedUser}
          clientMode={clientMode}
        />
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the user "{selectedUser?.first_name} {selectedUser?.last_name}" 
              and remove their access to ThreadHeads. This action cannot be undone and the user 
              will need to be recreated if access is needed again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function RemoveFromOrgButton({ userId }: { userId: string }) {
  const { removeMembership } = useUserManagement();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="destructive" size="sm" onClick={() => setOpen(true)}>
        Remove from Org
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this user from the organization?</AlertDialogTitle>
            <AlertDialogDescription>
              This will revoke their access to this organization and its locations. The user account will not be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => removeMembership.mutate({ userId }, { onSuccess: () => setOpen(false) })}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
